import { useQuery } from '@tanstack/react-query';
import coSellClient from 'packages/cosell/api/coSellClient';
import { useLocation } from 'react-router-dom';
import {
  PAGE_SIZE_KEY,
  SEARCH_KEY,
  FROM_KEY,
  CLOUD_STATUS_KEY,
  TAB_KEY,
  SORT_FIELD_KEY,
  SORT_ORDER_KEY,
  SortDirection,
  InvitationColumnKey,
  DEFAULT_INVITATIONS_SORT_DIRECTION,
  DEFAULT_INVITATIONS_SORT_KEY,
  START_DATE_URL_KEY,
  END_DATE_URL_KEY,
} from '../pages/CoSellLandingPage/helpers/urlParamsHelper';
import { useMemo } from 'react';
import { getActivePanelFilters } from '../utilities/filters/utils';

// Constants
const QUERY_KEY = 'cosell-invitations-list';
const STALE_TIME = 30000;

const useInvitationsListQuery = () => {
  const location = useLocation();

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const urlParams = useMemo(
    () => ({
      q: searchParams.get(SEARCH_KEY),
      pageSize: searchParams.get(PAGE_SIZE_KEY),
      from: searchParams.get(FROM_KEY),
      cloudStatus: searchParams.get(CLOUD_STATUS_KEY),
      tabKey: searchParams.get(TAB_KEY),
      sortFieldKey: (searchParams.get(SORT_FIELD_KEY) ||
        DEFAULT_INVITATIONS_SORT_KEY) as InvitationColumnKey,
      sortOrder: (searchParams.get(SORT_ORDER_KEY) ||
        DEFAULT_INVITATIONS_SORT_DIRECTION) as SortDirection,
      startDate: searchParams.get(START_DATE_URL_KEY),
      endDate: searchParams.get(END_DATE_URL_KEY),
    }),
    [searchParams],
  );

  // Memoize sort state to prevent unnecessary query updates
  const sortState = useMemo(
    () => ({
      sortKey: urlParams.sortFieldKey,
      sortDirection: urlParams.sortOrder,
    }),
    [urlParams.sortFieldKey, urlParams.sortOrder],
  );

  const activePanelFilters = useMemo(
    () => getActivePanelFilters({ searchParams, isInvitations: true }) || [],
    [searchParams],
  );

  const {
    data: invitationsList,
    error: invitationsListError,
    isLoading: isInvitationsListLoading,
  } = useQuery({
    queryKey: [
      QUERY_KEY,
      'ace',
      {
        ...urlParams,
        activePanelFilters,
        sortState,
      },
    ],
    queryFn: () =>
      coSellClient.getInvitationsList({
        q: urlParams.q,
        pageSize: urlParams.pageSize,
        from: urlParams.from,
        activePanelFilters,
        sortState,
        startDate: urlParams.startDate,
        endDate: urlParams.endDate,
      }),
    staleTime: STALE_TIME,
    enabled: urlParams.tabKey === 'invitations',
    refetchOnWindowFocus: false,
  });

  return {
    invitationsList,
    invitationsListError,
    isInvitationsListLoading,
    total: invitationsList?.total,
    isNextPage: invitationsList?.isNextPage,
    currentPageCount: invitationsList?.currentPageCount,
    nextIndex: invitationsList?.nextIndex,
    activePanelFilters,
    sortFieldKey: urlParams.sortFieldKey,
    sortOrder: urlParams.sortOrder,
    sortState,
  };
};

export default useInvitationsListQuery;
